import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Factory, MapPin, Users, TrendingUp, BarChart3, Edit, Trash2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { ViewToggle } from '../components/ui/view-toggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const Factories = () => {
  const [factories, setFactories] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [deleteFactoryId, setDeleteFactoryId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    manager: '',
    capacity: '',
    criar_local_estoque: true
  });

  useEffect(() => {
    document.title = "Gestão de Fábricas — NautilusOS";
    fetchData();
    
    // Timeout de segurança para o loading
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  const fetchData = async () => {
    try {
      const factoriesRes = await api.get('/factories');
      setFactories(factoriesRes.data);
      
      // Try to fetch comparison, but don't fail if it doesn't exist
      try {
        const comparisonRes = await api.get('/dashboard/factory-comparison');
        setComparison(comparisonRes.data);
      } catch (compError) {
        setComparison([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar fábricas');
      console.error('Error loading factories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (factory) => {
    setEditingFactory(factory);
    setFormData({
      name: factory.name,
      location: factory.location,
      manager: factory.manager,
      capacity: factory.capacity.toString(),
      criar_local_estoque: factory.criar_local_estoque !== false
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitLoading) return; // Prevenir duplo clique
    
    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        capacity: parseInt(formData.capacity)
      };

      if (editingFactory) {
        await api.put(`/factories/${editingFactory.id}`, payload);
        toast.success('Fábrica atualizada com sucesso!');
        
        // Atualizar apenas esta fábrica na lista
        setFactories(factories.map(f => 
          f.id === editingFactory.id ? {...f, ...payload} : f
        ));
      } else {
        const response = await api.post('/factories', payload);
        toast.success('Fábrica criada com sucesso!');
        
        // Adicionar nova fábrica à lista sem refetch
        setFactories([...factories, response.data]);
      }
      
      setShowDialog(false);
      setEditingFactory(null);
      setFormData({ name: '', location: '', manager: '', capacity: '', criar_local_estoque: true });
      
      // NÃO chamar fetchData() - atualização local é mais rápida
      
    } catch (error) {
      toast.error(editingFactory ? 'Erro ao atualizar fábrica' : 'Erro ao criar fábrica');
      console.error('Error saving factory:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/factories/${deleteFactoryId}`);
      toast.success('Fábrica excluída com sucesso!');
      setDeleteFactoryId(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir fábrica');
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingFactory(null);
    setFormData({ name: '', location: '', manager: '', capacity: '', criar_local_estoque: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="factories-page">
      <div className="flex items-center justify-between">
        <div>
          <TitleWithHelp
            title="Gestão de Fábricas"
            description="Cadastro das unidades produtivas. Cada fábrica pode ter projetos e fases designadas. O controle de acesso dos usuários é baseado nas fábricas permitidas."
            connections={['Usuários', 'Projetos', 'Produção']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Factory className="h-6 w-6 text-blue-600" />
              Gestão de Fábricas
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Gerencie múltiplas unidades produtivas</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <Button 
            className="bg-blue-600 hover:bg-blue-700" 
            data-testid="new-factory-button"
            onClick={() => setShowDialog(true)}
          >
            <Factory className="mr-2" size={20} />
            Nova Fábrica
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <Card data-testid="fabricas-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left p-3 font-medium text-gray-600">Nome</th>
                  <th className="text-left p-3 font-medium text-gray-600">Localização</th>
                  <th className="text-left p-3 font-medium text-gray-600">Gerente</th>
                  <th className="text-left p-3 font-medium text-gray-600">Capacidade</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-right p-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {factories.map(factory => (
                  <tr key={factory.id} className="border-b hover:bg-blue-50/40 transition-colors group" data-testid={`factory-row-${factory.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Factory className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{factory.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-gray-400" />
                        {factory.location}
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">{factory.manager}</td>
                    <td className="p-3 text-gray-600">{factory.capacity} un/mês</td>
                    <td className="p-3">
                      <Badge className={factory.status === 'operational' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {factory.status === 'operational' ? 'Operacional' : 'Manutenção'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(factory)} title="Editar">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteFactoryId(factory.id)} title="Excluir">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {factories.map(factory => (
          <Card key={factory.id} className="hover:shadow-lg transition-shadow" data-testid={`factory-card-${factory.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl">{factory.name}</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={factory.status === 'operational' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {factory.status === 'operational' ? 'Operacional' : 'Manutenção'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <MapPin size={16} className="mr-2 text-blue-600" />
                {factory.location}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Users size={16} className="mr-2 text-purple-600" />
                Gerente: {factory.manager}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <TrendingUp size={16} className="mr-2 text-green-600" />
                Capacidade: {factory.capacity} unidades/mês
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleEdit(factory)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteFactoryId(factory.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Dialog de Criação/Edição */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFactory ? 'Editar Fábrica' : 'Criar Nova Fábrica'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome da Fábrica</Label>
              <Input
                data-testid="factory-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Localização</Label>
              <Input
                data-testid="factory-location-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Gerente Responsável</Label>
              <Input
                data-testid="factory-manager-input"
                value={formData.manager}
                onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Capacidade (unidades/mês)</Label>
              <Input
                data-testid="factory-capacity-input"
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                required
              />
            </div>
            
            {/* Checkbox Local de Armazenamento */}
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-blue-50">
              <input
                type="checkbox"
                id="criar_local_estoque"
                checked={formData.criar_local_estoque}
                onChange={(e) => setFormData({ ...formData, criar_local_estoque: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <Label htmlFor="criar_local_estoque" className="cursor-pointer font-medium">
                  Local de Armazenamento
                </Label>
                <p className="text-xs text-gray-600">
                  Criar automaticamente um local de estoque para esta fábrica
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700" 
                data-testid="create-factory-submit"
                disabled={submitLoading}
              >
                {submitLoading ? 'Salvando...' : (editingFactory ? 'Salvar Alterações' : 'Criar Fábrica')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gráfico removido temporariamente para melhor performance */}
      {comparison.length > 0 && false && (
        <Card data-testid="factory-comparison-chart">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="mr-2" size={24} />
              Comparativo de Performance entre Fábricas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={comparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="factory_name" angle={-15} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="on_time_rate" name="Taxa On-Time (%)" fill="#0052CC" />
                <Bar dataKey="completion_rate" name="Taxa de Conclusão (%)" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteFactoryId} onOpenChange={() => setDeleteFactoryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta fábrica? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Factories;
